import { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  RefreshControl,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useThemeColors } from "@/providers/ThemeProvider";
import { FamilyWeekend } from "@/types";

/**
 * Weekend list item component
 */
interface WeekendItemProps {
  weekend: FamilyWeekend;
  onPress: () => void;
}

const WeekendItem = ({ weekend, onPress }: WeekendItemProps) => {
  const colors = useThemeColors();

  return (
    <Pressable
      onPress={onPress}
      className="px-4 py-4 flex-row items-center active:opacity-70"
      style={{
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
      }}
      accessibilityRole="button"
      accessibilityLabel={`Bekijk ${weekend.name}`}
    >
      <View
        className="w-12 h-12 rounded-full items-center justify-center mr-3"
        style={{ backgroundColor: colors.primaryLight }}
      >
        <Text className="text-xl">🎉</Text>
      </View>
      <View className="flex-1">
        <View className="flex-row items-center">
          <Text className="text-base font-semibold" style={{ color: colors.text }}>
            {weekend.name}
          </Text>
          {weekend.is_active && (
            <View className="ml-2 bg-green-100 px-2 py-0.5 rounded-full">
              <Text className="text-xs text-green-700 font-medium">Actief</Text>
            </View>
          )}
        </View>
        <Text className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
          {weekend.year}
        </Text>
        {weekend.theme && (
          <Text className="text-xs mt-0.5" style={{ color: colors.primary }}>
            {weekend.theme}
          </Text>
        )}
        {weekend.location_name && (
          <View className="flex-row items-center mt-1">
            <Ionicons name="location-outline" size={12} color={colors.textTertiary} />
            <Text className="text-xs ml-1" style={{ color: colors.textTertiary }}>
              {weekend.location_name}
            </Text>
          </View>
        )}
      </View>
      <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
    </Pressable>
  );
};

/**
 * All weekends list screen
 */
export default function WeekendsScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const [weekends, setWeekends] = useState<FamilyWeekend[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Fetch all finalized weekends
   */
  const fetchWeekends = async () => {
    try {
      const { data, error } = await supabase
        .from("family_weekends")
        .select("*")
        .eq("is_finalized", true)
        .order("year", { ascending: false });

      if (!error && data) {
        setWeekends(data);
      }
    } catch (error) {
      console.error("Error fetching weekends:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchWeekends();
  }, []);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchWeekends();
  };

  /**
   * Handle view weekend
   */
  const handleViewWeekend = (weekend: FamilyWeekend) => {
    router.push(`/(tabs)/profile/weekend/${weekend.id}`);
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "bottom"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Weekenden laden...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top", "bottom"]}
    >
      {/* Header */}
      <View
        className="px-4 py-4 flex-row items-center"
        style={{
          backgroundColor: colors.surface,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        }}
      >
        <Pressable
          onPress={() => router.back()}
          className="mr-3"
          accessibilityRole="button"
          accessibilityLabel="Terug"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </Pressable>
        <Text className="text-xl font-bold" style={{ color: colors.text }}>
          Vorige Weekenden
        </Text>
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {weekends.length > 0 ? (
          weekends.map((weekend) => (
            <WeekendItem
              key={weekend.id}
              weekend={weekend}
              onPress={() => handleViewWeekend(weekend)}
            />
          ))
        ) : (
          <View className="flex-1 items-center justify-center py-12">
            <Ionicons name="calendar-outline" size={48} color={colors.border} />
            <Text className="mt-2" style={{ color: colors.textTertiary }}>
              Nog geen weekenden
            </Text>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
